import { useRef, useState } from 'react';
import type { App, EnvVisibility } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvVarMutations } from '@/api/hooks';
import type { EnvImportResult } from '@/api/schemas';
import { importRequestBody, importSummaryText, importTargetLabel, type ImportConflict } from './importModel';
import type { ScopeSelection } from './ScopePicker';

interface Args {
  projectId: string;
  scope: ScopeSelection;
  apps: App[] | undefined;
  onDone: () => void;
}

/** What the user typed plus the dry-run preview (which any edit of the document invalidates). */
function useImportDraft() {
  const [content, setContentRaw] = useState('');
  const [defaultVisibility, setDefaultVisibility] = useState<EnvVisibility>('sensitive');
  const [onConflict, setOnConflict] = useState<ImportConflict>('new_version');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<EnvImportResult | null>(null);
  const setContent = (value: string) => {
    setContentRaw(value);
    setPreview(null);
  };
  return { content, setContent, defaultVisibility, setDefaultVisibility, onConflict, setOnConflict, note, setNote, preview, setPreview };
}

/** Paste a dotenv document → dry run → import: state and the two requests. */
export function useImportForm({ projectId, scope, apps, onDone }: Args) {
  const { importDotenv } = useEnvVarMutations();
  const draft = useImportDraft();
  const [problem, setProblem] = useState<unknown>(null);
  const [submitted, setSubmitted] = useState(false);
  const idempotencyKey = useRef(crypto.randomUUID());
  const app = apps?.find((a) => a.slug === scope.appSlug);
  const error = submitted && !draft.content.trim() ? 'Paste a .env document' : undefined;

  const applied = (result: EnvImportResult) => {
    idempotencyKey.current = crypto.randomUUID();
    toast.success('Import applied', { description: importSummaryText(result.summary) });
    onDone();
  };
  const run = async (dryRun: boolean) => {
    setSubmitted(true);
    setProblem(null);
    if (!draft.content.trim()) return;
    try {
      const body = importRequestBody({ scope, projectId, app, draft, dryRun });
      const result = await importDotenv.mutateAsync({ body, idempotencyKey: dryRun ? undefined : idempotencyKey.current });
      if (dryRun) draft.setPreview(result);
      else applied(result);
    } catch (e) {
      setProblem(e);
    }
  };
  return { ...draft, problem, error, isPending: importDotenv.isPending, targetLabel: importTargetLabel(scope, app), run };
}

export type ImportFormApi = ReturnType<typeof useImportForm>;
